// api/openLeak/index.ts
import apiInstance from "shared/api/axiosConfig";
import { decryptApiBodyToJson, encryptApiBodyFromJSON } from "shared/api/cryptApiBody";
import { DataLeak, LeakFilters, LeakStats, FollowAlert } from '../types/openLeaks.types';

/**
 * Récupérer toutes les fuites avec filtres
 * @param filters - Filtres optionnels (search, status, dates)
 */
export async function getLeaks(filters?: LeakFilters) {
  try {
    const response = await apiInstance.post("/open-leaks/leaks",
      {
        encrypt: encryptApiBodyFromJSON(filters || {})
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur getLeaks:', e);
    throw e;
  }
}

/**
 * Récupérer une fuite par ID
 * @param id - ID de la fuite
 */
export async function getLeakById(id: number) {
  try {
    const response = await apiInstance.post("/open-leaks/leaks/get-by-id",
      {
        encrypt: encryptApiBodyFromJSON({ id })
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur getLeakById ${id}:`, e);
    throw e;
  }
}

/**
 * Récupérer les statistiques
 */
export async function getLeaksStats() {
  try {
    const response = await apiInstance.post("/open-leaks/stats",
      {},
    );
    const result = decryptApiResponse(response.data.encrypt);
    return result.data;
  } catch (e) {
    console.error('Erreur getLeaksStats:', e);
    throw e;
  }
}

/**
 * Créer une alerte email pour suivre une entreprise
 * @param email - Email de l'utilisateur
 * @param company - Nom de l'entreprise à suivre
 */
export async function createFollowAlert(email: string, company: string) {
  try {
    const response = await apiInstance.post("/open-leaks/follow",
      {
        encrypt: encryptApiBodyFromJSON({ email, company })
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur createFollowAlert:', e);
    throw e;
  }
}

/**
 * Supprimer une alerte
 * @param id - ID de l'alerte
 */
export async function deleteFollowAlert(id: number) {
  try {
    const response = await apiInstance.delete("/open-leaks/follow", {
      data: { encrypt: encryptApiBodyFromJSON({ id }) }
    });
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur deleteFollowAlert ${id}:`, e);
    throw e;
  }
}

/**
 * Récupérer les alertes d'un email
 * @param email - Email de l'utilisateur
 */
export async function getFollowAlertsByEmail(email: string) {
  try {
    const response = await apiInstance.post("/open-leaks/follow/list",
      {
        encrypt: encryptApiBodyFromJSON({ email })
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error('Erreur getFollowAlertsByEmail:', e);
    throw e;
  }
}

/**
 * Vérifier si une entreprise a des fuites
 * @param company - Nom de l'entreprise
 */
export async function checkCompanyLeaks(company: string) {
  try {
    const response = await apiInstance.post("/open-leaks/check-company",
      {
        encrypt: encryptApiBodyFromJSON({ company })
      },
    );
    return decryptApiResponse(response.data.encrypt);
  } catch (e) {
    console.error(`Erreur checkCompanyLeaks ${company}:`, e);
    throw e;
  }
}

// api/openLeak/index.ts - Ajouter ces fonctions

/**
 * Lier une fuite au client connecté
 * @param breachId - ID de la fuite (Breach)
 * @param notes - Notes optionnelles
 * @param userType
 */
export async function linkBreachToClient(breachId: number, notes?: string, userType = 'individual') {
  try {
    const response = await apiInstance.post("/client/breaches/link",
      { breachId, notes, type: userType },
      { withCredentials: true }
    );
    return response.data;
  } catch (e) {
    console.error('Erreur linkBreachToClient:', e);
    throw e;
  }
}

/**
 * Vérifier si le client a déjà lié une fuite
 * @param breachId - ID de la fuite (Breach)
 * @param userType
 */
export async function checkClientBreachLink(breachId: number, userType = 'individual') {
  try {
    const response = await apiInstance.post(`/client/breaches/link/check/${breachId}`,
      {type: userType },
      { withCredentials: true }
    );
    return response.data;
  } catch (e) {
    console.error('Erreur checkClientBreachLink:', e);
    throw e;
  }
}

/**
 * Fonction utilitaire pour déchiffrer la réponse de l'API
 */
export function decryptApiResponse(encryptedData: string): any {
  if (!encryptedData) return null;
  try {
    return decryptApiBodyToJson(encryptedData);
  } catch (e) {
    console.error('Erreur decryptApiResponse:', e);
    return null;
  }
}